/**
 * Utility functions for synchronizing meetup component with waypoints
 */

import { WaypointResponse } from '@/shared/api/routes'

/**
 * Check if a string is a coordinate pair (e.g., "47.09547, -122.30464")
 */
export function isCoordinateString(str: string | null | undefined): boolean {
  if (!str) return false
  const coordMatch = str.trim().match(/^([+-]?\d+\.?\d*),\s*([+-]?\d+\.?\d*)$/)
  return coordMatch !== null
}

/**
 * Parse coordinates from a string
 * Returns { latitude, longitude } or null if invalid
 */
export function parseCoordinates(str: string | null | undefined): { latitude: number; longitude: number } | null {
  if (!str) return null
  
  const coordMatch = str.trim().match(/^([+-]?\d+\.?\d*),\s*([+-]?\d+\.?\d*)$/)
  if (!coordMatch) return null
  
  const lat = parseFloat(coordMatch[1])
  const lon = parseFloat(coordMatch[2])
  
  if (isNaN(lat) || isNaN(lon)) return null
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null
  
  return { latitude: lat, longitude: lon }
}

/**
 * Format waypoint location for meetup_location field
 * Uses poi_address if available, otherwise formats coordinates
 */
export function formatWaypointLocationForMeetup(waypoint: WaypointResponse): string | null {
  // Prefer address if available
  if (waypoint.poi_address) {
    return waypoint.poi_address
  }
  
  // Fall back to coordinates if available
  if (waypoint.latitude != null && waypoint.longitude != null) {
    return `${waypoint.latitude}, ${waypoint.longitude}`
  }
  
  // Use waypoint name as last resort if it looks like coordinates
  if (waypoint.name && isCoordinateString(waypoint.name)) {
    return waypoint.name
  }
  
  return null
}

/**
 * Find meetup waypoint(s) in a list of waypoints
 */
export function findMeetupWaypoints(waypoints: WaypointResponse[]): WaypointResponse[] {
  return waypoints.filter(wp => wp.poi_type === 'meetup_spot')
}

/**
 * Collect all waypoints from trip payload (route schedule preferred, then flat trip.waypoints).
 * Matches TripViewPage / itinerary guest views.
 */
export function getAllWaypointsFromTrip(trip: {
  routeSchedule?: { days?: Array<{ dayIndex?: number; routes?: Array<{ waypoints?: WaypointResponse[] }> }> }
  waypoints?: Array<WaypointResponse | { waypoint?: WaypointResponse; id?: string; trip_day?: number; order_index?: number }>
} | null | undefined): WaypointResponse[] {
  if (!trip) return []

  if (trip.routeSchedule?.days) {
    const waypoints: WaypointResponse[] = []
    trip.routeSchedule.days.forEach((day) => {
      day.routes?.forEach((route) => {
        if (route.waypoints && Array.isArray(route.waypoints)) {
          route.waypoints.forEach((wp) => {
            if (!waypoints.find((w) => w.id === wp.id)) {
              const tripDay = wp.trip_day ?? (day.dayIndex !== undefined ? day.dayIndex + 1 : 1)
              waypoints.push({
                ...wp,
                trip_day: tripDay,
                order_index: wp.order_index ?? 0
              })
            }
          })
        }
      })
    })
    return waypoints.sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
  }

  const flat: WaypointResponse[] = (trip.waypoints || []).map((wp) => {
    const waypointData = 'waypoint' in wp && wp.waypoint ? wp.waypoint : wp
    const w = waypointData as WaypointResponse
    return {
      ...w,
      id: w.id || (wp as { id?: string }).id || '',
      name: w.name,
      latitude: w.latitude,
      longitude: w.longitude,
      poi_type: w.poi_type,
      poi_address: w.poi_address,
      trip_day: (wp as { trip_day?: number }).trip_day ?? w.trip_day ?? 1,
      order_index: (wp as { order_index?: number }).order_index ?? w.order_index ?? 0,
      start_time: w.start_time,
      end_time: w.end_time,
      created_at: w.created_at || new Date().toISOString()
    } as WaypointResponse
  })
  return flat.sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
}

/**
 * Normalize time string to HH:MM format for backend validation
 * Handles HH:MM:SS (database format), HH:MM, and other formats
 * Returns null if time cannot be normalized
 */
export function normalizeTimeToHHMM(timeString: string | null | undefined): string | null {
  if (!timeString) return null

  const trimmed = timeString.trim()

  // Already in HH:MM format
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed
  }

  // Handle HH:MM:SS format (database TIME type)
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (match) {
    const hours = parseInt(match[1], 10)
    const minutes = parseInt(match[2], 10)

    if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    }
  }

  // Handle HHMM format (4 digits)
  if (/^\d{4}$/.test(trimmed)) {
    const hours = parseInt(trimmed.substring(0, 2), 10)
    const minutes = parseInt(trimmed.substring(2, 4), 10)

    if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    }
  }

  return null
}

export interface ResolvedMeetupDisplay {
  /** Primary line: address, coordinates string, or waypoint name */
  locationLabel: string | null
  timeFormatted: string | null
  /** True when locationLabel is a lat,lng pair */
  locationIsCoordinates: boolean
  /** Prefer for Maps / copy when waypoint has lat/lng */
  coordsForMaps: string | null
  meetupWaypoints: WaypointResponse[]
  source: 'waypoint' | 'trip_fields'
}

/**
 * Format time for meetup display (trip.meetup_time or waypoint start/end time).
 */
export function formatMeetupTimeDisplay(timeString: string | null | undefined): string | null {
  if (!timeString) return null
  const normalized = normalizeTimeToHHMM(timeString) || timeString
  try {
    const [hours, minutes] = normalized.split(':').map((part) => Number.parseInt(part, 10))
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
    const date = new Date()
    date.setHours(hours, minutes, 0, 0)
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  } catch {
    return null
  }
}

/**
 * Resolve meetup for guest/share views: prefer itinerary waypoint tagged `meetup_spot`,
 * fall back to legacy `trip.meetup_location` / `trip.meetup_time`.
 */
export function resolveMeetupDisplay(
  trip: { meetup_location?: string | null; meetup_time?: string | null },
  allWaypoints: WaypointResponse[]
): ResolvedMeetupDisplay {
  const meetupWaypoints = findMeetupWaypoints(allWaypoints)
  const primary = meetupWaypoints[0]

  if (primary) {
    const locationLabel =
      formatWaypointLocationForMeetup(primary) ||
      (primary.name?.trim() ? primary.name.trim() : null)

    const coordsForMaps =
      primary.latitude != null && primary.longitude != null
        ? `${primary.latitude}, ${primary.longitude}`
        : locationLabel && isCoordinateString(locationLabel)
          ? locationLabel
          : null

    const locationIsCoordinates = Boolean(
      (locationLabel && isCoordinateString(locationLabel)) ||
        (primary.latitude != null && primary.longitude != null)
    )

    const rawTime = primary.start_time || primary.end_time || trip.meetup_time
    const timeFormatted =
      formatMeetupTimeDisplay(rawTime ?? undefined) ||
      (trip.meetup_time ? formatMeetupTimeDisplay(trip.meetup_time) : null)

    return {
      locationLabel,
      timeFormatted,
      locationIsCoordinates,
      coordsForMaps,
      meetupWaypoints,
      source: 'waypoint'
    }
  }

  const legacyLoc = trip.meetup_location ?? null
  return {
    locationLabel: legacyLoc,
    timeFormatted: trip.meetup_time ? formatMeetupTimeDisplay(trip.meetup_time) : null,
    locationIsCoordinates: legacyLoc ? isCoordinateString(legacyLoc) : false,
    coordsForMaps: legacyLoc && isCoordinateString(legacyLoc) ? legacyLoc : null,
    meetupWaypoints: [],
    source: 'trip_fields'
  }
}
